import React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';
import styled from 'styled-components';
import { accordionDown, accordionUp } from '../../styles/animations';

const Accordion = AccordionPrimitive.Root;

const StyledItem = styled(AccordionPrimitive.Item)`
  border-bottom: 1px solid ${props => props.theme.colors.border.main};
`;

const StyledHeader = styled(AccordionPrimitive.Header)`
  display: flex;
`;

const StyledTrigger = styled(AccordionPrimitive.Trigger)`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.lg} 0;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  transition: ${props => props.theme.transitions.default};
  color: ${props => props.theme.colors.text.primary};
  background: none;
  border: none;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
  
  &[data-state='open'] svg {
    transform: rotate(180deg);
  }
  
  svg {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
    transition: transform 200ms;
    color: ${props => props.theme.colors.text.primary};
  }
  
  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary.main};
    outline-offset: 2px;
  }
`;

const StyledContent = styled(AccordionPrimitive.Content)`
  overflow: hidden;
  font-size: ${props => props.theme.typography.fontSize.sm};
  transition: all 200ms;
  
  &[data-state='open'] {
    animation: ${accordionDown} 200ms ease-out;
  }
  
  &[data-state='closed'] {
    animation: ${accordionUp} 200ms ease-out;
  }
`;

const ContentWrapper = styled.div`
  padding-bottom: ${props => props.theme.spacing.lg};
  padding-top: 0;
  color: ${props => props.theme.colors.text.muted};
  line-height: 1.6;
`;

const AccordionItem = React.forwardRef((props, ref) => (
  <StyledItem ref={ref} {...props} />
));
AccordionItem.displayName = AccordionPrimitive.Item.displayName;

const AccordionTrigger = React.forwardRef(({ children, ...props }, ref) => (
  <StyledHeader>
    <StyledTrigger ref={ref} {...props}>
      {children}
      <ChevronDown />
    </StyledTrigger>
  </StyledHeader>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef(({ children, className, ...props }, ref) => (
  <StyledContent ref={ref} {...props}>
    <ContentWrapper className={className}>
      {children}
    </ContentWrapper>
  </StyledContent>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
